import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  AlertTitle,
  Divider,
  IconButton,
  LinearProgress,
  Avatar,
  Tooltip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import TodayIcon from '@mui/icons-material/Today';
import UpdateIcon from '@mui/icons-material/Update';
import EventIcon from '@mui/icons-material/Event';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import PersonIcon from '@mui/icons-material/Person';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { sectionTitleStyles } from '@Pages/RnDMember/ExitRequestResponseUnified/ExitRequestResponseStyles';

// Local imports
import { ApprovalCommentPopup } from './ApprovalCommentPopup';
import {
  LABELS,
  ICON_SIZES
} from './PlanStatusTab.constants';
import {
  formatDate,
  getStatusConfig,
  getApprovalConfig,
  getAlertConfig
} from './PlanStatusTab.utils';

export const PlanStatusTab = ({
                                createdAt,
                                lastUpdatedAt,
                                status,
                                approved,
                                approvedBy,
                                approvalDate,
                                approvalComment,
                                approvals = [],
                                approvingManagers = [],
                                totalApprovers = 0,
                                approvedCount = 0,
                                planType,
                                localRiskAssessment,
                                localRiskAssessmentBy,
                                localRiskAssessmentDate,
                                theme
                              }) => {
  // ================ Local State ================
  const [popupOpen, setPopupOpen] = useState(false);

  // ================ Render Functions ================
  const renderStatusChip = (status) => {
    const config = getStatusConfig(status);
    const iconComponent = config.icon === 'CheckCircleIcon' ? <CheckCircleIcon /> :
        config.icon === 'WarningIcon' ? <WarningIcon /> : null;
  console.log("plan status tab", status, config);
    return (
        <Chip
            size="small"
            label={config.label}
            color={config.color}
            icon={iconComponent}
            sx={styles.chip}
        />
    );
  };

  const renderApprovalChip = (approved) => {
    const config = getApprovalConfig(approved);
    const iconComponent = config.icon === 'CheckCircleIcon' ? <CheckCircleIcon /> :
        config.icon === 'WarningIcon' ? <WarningIcon /> : null;

    return (
        <Chip
            size="small"
            label={config.label}
            color={config.color}
            icon={iconComponent}
            sx={styles.chip}
        />
    );
  };

  const renderStatusAlert = (status) => {
    const config = getAlertConfig(status, planType);
    if (!config) return null;

    const iconComponent = config.icon === 'CheckCircleIcon' ? <CheckCircleIcon /> :
        config.icon === 'WarningIcon' ? <WarningIcon /> : undefined;

    return (
        <Alert
            severity={config.severity}
            icon={iconComponent}
            sx={styles.alert}
        >
          <AlertTitle>{config.title}</AlertTitle>
          {config.content}
        </Alert>
    );
  };

  const renderTimelineInfo = () => (
      <Box sx={styles.section}>
        <Typography variant="subtitle2" sx={styles.sectionTitle(theme)}>
          {LABELS.TIMELINE_INFO}
        </Typography>

        <Box sx={styles.infoRow}>
          <TodayIcon sx={styles.timelineIcon(theme)} />
          <Typography variant="body2" color="textSecondary" sx={styles.labelText}>
            {LABELS.CREATED}
          </Typography>
          <Typography variant="body2" sx={styles.valueText}>
            {formatDate(createdAt)}
          </Typography>
        </Box>

        <Box sx={styles.infoRow}>
          <UpdateIcon sx={styles.timelineIcon(theme)} />
          <Typography variant="body2" color="textSecondary" sx={styles.labelText}>
            {LABELS.LAST_UPDATED}
          </Typography>
          <Typography variant="body2" sx={styles.valueText}>
            {formatDate(lastUpdatedAt)}
          </Typography>
        </Box>

        {approvalDate && (
            <Box sx={styles.infoRow}>
              <EventIcon sx={styles.approvalDateIcon(theme)} />
              <Typography variant="body2" color="textSecondary" sx={styles.labelText}>
                {LABELS.APPROVAL_DATE}
              </Typography>
              <Typography variant="body2" sx={styles.valueText}>
                {formatDate(approvalDate)}
              </Typography>
            </Box>
        )}
      </Box>
  );

  const renderApprovalDetails = () => {
    if (!approvedBy && !approvalComment) return null;

    return (
        <Box sx={styles.section}>
          <Typography variant="subtitle2" sx={styles.sectionTitle(theme)}>
            {LABELS.APPROVAL_DETAILS}
          </Typography>

          {approvedBy && (
              <Box sx={styles.infoRow}>
                <PersonIcon sx={styles.personIcon(theme)} />
                <Typography variant="body2" sx={styles.nameText}>
                  {approvedBy.name || approvedBy.username}
                </Typography>
                {approvedBy.email && (
                    <Typography variant="body2" sx={styles.emailText(theme)}>
                      {approvedBy.email}
                    </Typography>
                )}
                {approvedBy.role && (
                    <Chip
                        size="small"
                        label={approvedBy.role}
                        sx={styles.roleChip(theme)}
                    />
                )}
              </Box>
          )}

          {approvalComment && (
              <Box sx={styles.commentContainer}>
                <Typography variant="body2" sx={styles.commentLabel}>
                  {LABELS.APPROVAL_COMMENT}
                  <IconButton
                      size="small"
                      sx={styles.expandButton(theme)}
                      onClick={() => setPopupOpen(true)}
                      aria-label={LABELS.EXPAND_COMMENT}
                  >
                    <OpenInFullIcon fontSize="small" />
                  </IconButton>
                </Typography>
                <Box sx={styles.commentBox(theme)}>
                  {approvalComment}
                </Box>
              </Box>
          )}
        </Box>
    );
  };

  const renderApprovalProgress = () => {
    if (totalApprovers === 0) return null;

    const progressPercent = totalApprovers > 0 ? (approvedCount / totalApprovers) * 100 : 0;
    const hasRejection = approvals.some(a => a.approved === false);
    // Approvers with no decision recorded yet, so they can be named rather than just counted.
    const decidedIds = approvals.map(a => String(a.approver?.id));
    const pendingApprovers = approvingManagers.filter(m => !decidedIds.includes(String(m.id)));
    const pendingCount = pendingApprovers.length || Math.max(0, totalApprovers - approvals.length);

    return (
        <Box sx={styles.section}>
          <Typography variant="subtitle2" sx={styles.sectionTitle(theme)}>
            Approval Progress
          </Typography>

          {/* Progress bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{ flex: 1, mr: 2 }}>
              <LinearProgress
                  variant="determinate"
                  value={hasRejection ? 100 : progressPercent}
                  color={hasRejection ? "error" : (progressPercent >= 100 ? "success" : "primary")}
                  sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 60, textAlign: 'right' }}>
              {hasRejection ? "Rejected" : `${approvedCount} / ${totalApprovers}`}
            </Typography>
          </Box>

          {/* Summary chips */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Chip
                size="small"
                icon={<CheckCircleIcon />}
                label={`${approvedCount} Approved`}
                color="success"
                variant="outlined"
            />
            {hasRejection && (
                <Chip
                    size="small"
                    icon={<CancelIcon />}
                    label={`${approvals.filter(a => a.approved === false).length} Rejected`}
                    color="error"
                    variant="outlined"
                />
            )}
            {pendingCount > 0 && !hasRejection && (
                <Chip
                    size="small"
                    icon={<HourglassEmptyIcon />}
                    label={`${pendingCount} Pending`}
                    color="default"
                    variant="outlined"
                />
            )}
          </Box>

          {/* Individual approval records */}
          {approvals.length > 0 && (
              <Box>
                {approvals.map((approval, index) => (
                    <Box
                        key={approval.id || index}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          p: 1.5,
                          mb: 1,
                          borderRadius: 1,
                          bgcolor: approval.approved
                              ? 'rgba(46, 125, 50, 0.04)'
                              : 'rgba(211, 47, 47, 0.04)',
                          border: '1px solid',
                          borderColor: approval.approved
                              ? 'rgba(46, 125, 50, 0.15)'
                              : 'rgba(211, 47, 47, 0.15)',
                        }}
                    >
                      <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            mr: 1.5,
                            bgcolor: approval.approved ? 'success.main' : 'error.main',
                            fontSize: '0.85rem'
                          }}
                      >
                        {approval.approver?.username?.charAt(0)?.toUpperCase() || '?'}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {approval.approver?.username || 'Unknown'}
                          </Typography>
                          <Chip
                              size="small"
                              label={approval.approved ? "Approved" : "Rejected"}
                              color={approval.approved ? "success" : "error"}
                              sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        </Box>
                        {approval.approver?.email && (
                            <Typography variant="caption" color="textSecondary">
                              {approval.approver.email}
                            </Typography>
                        )}
                        {approval.comment && (
                            <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.85rem', fontStyle: 'italic' }}>
                              "{approval.comment}"
                            </Typography>
                        )}
                        {approval.approvalDate && (
                            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                              {formatDate(approval.approvalDate)}
                            </Typography>
                        )}
                      </Box>
                    </Box>
                ))}
              </Box>
          )}

          {/* Approvers who have not decided yet */}
          {!hasRejection && pendingApprovers.length > 0 && (
              <Box>
                {pendingApprovers.map((manager, index) => (
                    <Box
                        key={manager.id || index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 1.5,
                          mb: 1,
                          borderRadius: 1,
                          bgcolor: 'rgba(0, 0, 0, 0.02)',
                          border: '1px dashed',
                          borderColor: 'rgba(0, 0, 0, 0.18)',
                        }}
                    >
                      <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            mr: 1.5,
                            bgcolor: 'grey.400',
                            fontSize: '0.85rem'
                          }}
                      >
                        {manager.username?.charAt(0)?.toUpperCase() || '?'}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {manager.username || 'Unknown'}
                          </Typography>
                          <Chip
                              size="small"
                              icon={<HourglassEmptyIcon sx={{ fontSize: '0.8rem' }} />}
                              label="Pending"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        </Box>
                        {manager.email && (
                            <Typography variant="caption" color="textSecondary">
                              {manager.email}
                            </Typography>
                        )}
                      </Box>
                    </Box>
                ))}
              </Box>
          )}
        </Box>
    );
  };

  // ================ Main Render ================
  return (
      <Box sx={styles.container}>
        <Typography variant="h6" sx={sectionTitleStyles(theme)}>
          {LABELS.PLAN_STATUS}
        </Typography>

        {/* Status Section */}
        <Box sx={styles.section}>
          <Box sx={styles.infoRow}>
            <Typography variant="body2" color="textSecondary" sx={styles.fieldLabel}>
              {LABELS.PLAN_STATUS_FIELD}
            </Typography>
            <Box>{renderStatusChip(status)}</Box>
          </Box>

          <Box sx={styles.infoRow}>
            <Typography variant="body2" color="textSecondary" sx={styles.fieldLabel}>
              {LABELS.APPROVAL_FIELD}
            </Typography>
            <Box>{renderApprovalChip(approved)}</Box>
          </Box>
        </Box>

        <Divider sx={styles.divider} />

        {/* Timeline Information */}
        {renderTimelineInfo()}

        <Divider sx={styles.divider} />

        {/* Approval Details */}
        {renderApprovalDetails()}

        {localRiskAssessment && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {LABELS.LOCAL_RISK_ASSESSMENT}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{localRiskAssessment}</Typography>
              {localRiskAssessmentBy && (
                  <Typography variant="caption" color="textSecondary">
                    {LABELS.ASSESSED_BY} {localRiskAssessmentBy.username} — {formatDate(localRiskAssessmentDate)}
                  </Typography>
              )}
            </Box>
        )}

        {/* Multi-Approval Progress */}
        {totalApprovers > 0 && planType === 'EXEMPTION_REQUEST' && (
            <>
              <Divider sx={styles.divider} />
              {renderApprovalProgress()}
            </>
        )}

        {/* Status Alerts */}
        {status && (
            <Box sx={styles.alertContainer}>
              {renderStatusAlert(status)}
            </Box>
        )}

        {/* Approval Comment Popup */}
        <ApprovalCommentPopup
            open={popupOpen}
            onClose={() => setPopupOpen(false)}
            title={LABELS.APPROVAL_COMMENT.replace(':', '')}
            comment={approvalComment}
        />
      </Box>
  );
};

// ================ Component Styles ================
const styles = {
  container: {
    overflowY: 'auto',
    maxHeight: '70vh'
  },
  section: {
    mb: 2
  },
  sectionTitle: (theme) => ({
    mb: 1,
    color: theme.palette.primary.main
  }),
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    mb: 1
  },
  fieldLabel: {
    mr: 1,
    minWidth: '120px',
    fontSize: '0.95rem'
  },
  labelText: {
    mr: 1,
    minWidth: '100px',
    fontSize: '0.9rem'
  },
  valueText: {
    fontSize: '0.9rem'
  },
  nameText: {
    fontWeight: 500
  },
  emailText: (theme) => ({
    fontSize: '0.85rem',
    ml: 1,
    color: theme.palette.text.secondary
  }),
  chip: {
    fontWeight: 500
  },
  // ... continuing from previous
  roleChip: (theme) => ({
    ml: 1,
    fontSize: ICON_SIZES.CHIP,
    bgcolor: theme.palette.grey[100]
  }),
  timelineIcon: (theme) => ({
    mr: 1,
    fontSize: ICON_SIZES.TIMELINE,
    color: theme.palette.text.secondary
  }),
  approvalDateIcon: (theme) => ({
    mr: 1,
    fontSize: ICON_SIZES.TIMELINE,
    color: theme.palette.error.main
  }),
  personIcon: (theme) => ({
    fontSize: ICON_SIZES.PERSON,
    mr: 1,
    color: theme.palette.text.secondary
  }),
  divider: {
    my: 2
  },
  commentContainer: {
    mt: 1,
    position: 'relative'
  },
  commentLabel: {
    fontWeight: 500,
    fontSize: '0.9rem'
  },
  expandButton: (theme) => ({
    position: 'absolute',
    top: 18,
    right: 0,
    zIndex: 1,
    scale: '0.8',
    bgcolor: 'rgba(255,255,255,0.7)',
    '&:hover': {
      bgcolor: 'rgba(255,255,255,0.9)',
      color: theme.palette.primary.main
    }
  }),
  commentBox: (theme) => ({
    fontSize: '0.9rem',
    mb: 0.5,
    maxHeight: 100,
    overflowY: 'auto',
    bgcolor: theme.palette.action.hover,
    borderRadius: 1,
    p: 1,
    pr: 5,
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
  }),
  alertContainer: {
    mt: 2
  },
  alert: {
    fontSize: '0.85rem'
  }
};
